export const SETTINGS_STORAGE_KEY = 'sudoku.settings.v1';

export type Language = 'de' | 'en';
export type Theme = 'light' | 'dark';

export type AppSettingsV1 = {
  version: 1;
  language: Language;
  themeOverride?: Theme;
};

export const defaultSettings: AppSettingsV1 = {
  version: 1,
  language: 'de',
};

type ReadableStorage = Pick<Storage, 'getItem'>;
type WritableStorage = Pick<Storage, 'setItem'>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isLanguage(value: unknown): value is Language {
  return value === 'de' || value === 'en';
}

function isTheme(value: unknown): value is Theme {
  return value === 'light' || value === 'dark';
}

export function parseSettings(serialized: string | null): AppSettingsV1 {
  if (serialized === null) {
    return defaultSettings;
  }

  try {
    const value: unknown = JSON.parse(serialized);
    if (!isRecord(value) || value.version !== 1 || !isLanguage(value.language)) {
      return defaultSettings;
    }

    if (value.themeOverride === undefined) {
      return {
        version: 1,
        language: value.language,
      };
    }

    if (!isTheme(value.themeOverride)) {
      return defaultSettings;
    }

    return {
      version: 1,
      language: value.language,
      themeOverride: value.themeOverride,
    };
  } catch {
    return defaultSettings;
  }
}

export function loadSettings(storage: ReadableStorage): AppSettingsV1 {
  try {
    return parseSettings(storage.getItem(SETTINGS_STORAGE_KEY));
  } catch {
    return defaultSettings;
  }
}

export function saveSettings(storage: WritableStorage, settings: AppSettingsV1): boolean {
  try {
    storage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    return true;
  } catch {
    return false;
  }
}
